import * as THREE from 'three';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';

export default function logoRotateAnimation(
	containerName = 'data-rotate_icon', // ロゴを表示するデータ属性の名前
	enableMouseFollow = true // カーソル追従切り替え（ true = オン ）

) {

	/**
	 * ============================================
	 * Container
	 * ============================================
	 */

	const container = document.querySelector(`[${containerName}]`);

	// 要素が存在しなければ処理を終了
	if (!container) {
		return;
	}

	// 前回描画したcanvasが残っている場合は一度空にする
	container.innerHTML = '';

	/**
	 * ============================================
	 * Scene
	 * ============================================
	 */

	// Three.jsを作成する
	const scene = new THREE.Scene();

	/**
	 * ============================================
	 * Camera
	 * ============================================
	 */

	// どこから3D空間を見るかを決めるカメラ
	const camera = new THREE.PerspectiveCamera(
		35, // 視野角
		container.clientWidth / container.clientHeight, // 縦横比
		0.1, // 一番近くで描画する距離
		5000 // 一番遠くまで描画する距離
	);

	// カメラを手前に配置
	camera.position.set(0, 0, 1000);

	// 原点を見る
	camera.lookAt(0, 0, 0);

	/**
	 * ============================================
	 * Renderer
	 * ============================================
	 */

	// WebGLで3Dを描画するためのレンダラー
	const renderer = new THREE.WebGLRenderer({
		alpha: true, // 背景を透明にする
		antialias: true, // ジャギーを滑らかにする
	});

	// 高解像度ディスプレイ対応
	renderer.setPixelRatio(
		Math.min(window.devicePixelRatio, 2)
	);

	// コンテナと同じサイズで描画
	renderer.setSize(
		container.clientWidth,
		container.clientHeight
	);

	// canvasをHTMLへ追加
	container.appendChild(renderer.domElement);

	/**
	 * ============================================
	 * Light
	 * ============================================
	 */

	// 全体を均一に照らすライト
	const ambientLight = new THREE.AmbientLight(
		0xffffff,
		2
	);

	scene.add(ambientLight);

	// 正面右上から照らすライト
	const directionalLight =
		new THREE.DirectionalLight(
			0xffffff,
			4
		);

	directionalLight.position.set(
		200,
		200,
		300
	);

	scene.add(directionalLight);

	// 背面側からも少し光を当てる
	const backLight =
		new THREE.DirectionalLight(
			0xffffff,
			2
		);

	backLight.position.set(
		-200,
		-100,
		-300
	);

	scene.add(backLight);

	/**
	 * ============================================
	 * Group
	 * ============================================
	 */
	// 回転専用グループ
	// このグループを回転させることでロゴ全体が回転する
	const rotateGroup = new THREE.Group();
	scene.add(rotateGroup);

	// SVGから生成したロゴを格納するグループ
	const logoGroup = new THREE.Group();
	rotateGroup.add(logoGroup);

	/**
	 * ============================================
	 * Mouse
	 * ============================================
	 */
	// 現在の向き
	let currentMouseX = 0;
	let currentMouseY = 0;

	// 目標の向き
	let targetMouseX = 0;
	let targetMouseY = 0;

	// 追従範囲
	const mouseArea = container.closest('[data-rotate_icon-area]');

	const isPc = window.matchMedia('(min-width:768px)');

	const onMouseMove = (e) => {

		if (!isPc.matches || !mouseArea) return;

		const rect = mouseArea.getBoundingClientRect();

		// エリア内の座標
		const x = (e.clientX - rect.left) / rect.width;
		const y = (e.clientY - rect.top) / rect.height;

		// 0〜1 → -1〜1
		targetMouseX = x * 2 - 1;
		targetMouseY = y * 2 - 1;

	};

	if (enableMouseFollow) { // カーソル追従がオンの場合に実行

		mouseArea?.addEventListener('mousemove', onMouseMove);

		mouseArea?.addEventListener('mouseleave', () => {
			targetMouseX = 0;
			targetMouseY = 0;
		});

	}

	/**
	 * ============================================
	 * SVG Loader
	 * ============================================
	 */

	// SVGファイルを読み込む
	const loader = new SVGLoader();

	loader.load(

		'/wp-content/themes/reignite_theme_renewal/images/common/logo-rotate-animation.svg', // Canvasで使用するロゴの格納先

		(data) => {

			// ロゴの材質
			// 金属っぽい見た目になるよう設定
			const material =
				new THREE.MeshPhysicalMaterial({
					color: 0xffffff,
					metalness: 0.8,
					roughness: 0.15,
					clearcoat: 1,
					clearcoatRoughness: 0.1,
				});

			// SVG内の各Pathごとに処理
			data.paths.forEach((path) => {

				// PathをThree.jsで扱えるShapeへ変換
				const shapes =
					SVGLoader.createShapes(path);

				// Shapeごとに立体化する
				shapes.forEach((shape) => {

					// 厚みを持たせた3D形状を作成
					const geometry =
						new THREE.ExtrudeGeometry(
							shape,
							{
								depth: 100,
								bevelEnabled: true,
								bevelThickness: 2,
								bevelSize: 2,
								bevelSegments: 5,
							}
						);

					// 光の当たり方を計算
					geometry.computeVertexNormals();

					// ジオメトリとマテリアルを組み合わせてMeshを作る
					const mesh =
						new THREE.Mesh(
							geometry,
							material
						);

					// ロゴグループへ追加
					logoGroup.add(mesh);

				});

			});

			// SVGは上下が逆なためY方向を反転
			logoGroup.scale.set(
				1,
				-1,
				1
			);

			// ロゴ全体の大きさと中心位置を取得
			const box =
				new THREE.Box3().setFromObject(logoGroup);

			// コンテナサイズに合わせてロゴの大きさを調整
			const applyLogoLayout = () => {

				const baseSize =
					box.getSize(new THREE.Vector3());

				const baseCenter =
					box.getCenter(new THREE.Vector3());

				// 横幅・高さの大きい方を基準にする
				const maxSize =
					Math.max(baseSize.x, baseSize.y);

				// コンテナの約70%に収まるようにする
				const targetSize =
					Math.min(
						container.clientWidth,
						container.clientHeight
					) * 0.7;

				const scale =
					targetSize / maxSize;

				// 拡大縮小
				logoGroup.scale.set(
					scale,
					-scale,
					scale
				);

				// ロゴの中心を原点へ移動
				// logoGroup.position.set(
				// 	-baseCenter.x,
				// 	-baseCenter.y,
				// 	-baseCenter.z
				// );

				logoGroup.position.set(
					-baseCenter.x * scale,
					-baseCenter.y * scale,
					-baseCenter.z * scale
				);
			};

			// 最初に少し傾けた状態から開始
			rotateGroup.rotation.x = 0.4;
			rotateGroup.rotation.y = 0.4;

			// 画面幅768px以上かどうかを監視
			const breakPointPc = 768;

			const mql =
				window.matchMedia(
					`(min-width:${breakPointPc}px)`
				);

			let resizeTimer = null;

			// レイアウト更新
			const checkWindow = () => {
				// ロゴサイズを再計算
				applyLogoLayout();
				// レンダラー・カメラも更新
				onResize();
			};
			checkWindow();

			window.addEventListener('resize', () => {

				clearTimeout(resizeTimer);

				resizeTimer = setTimeout(() => {
					checkWindow();
				}, 200);

			});

			// 画面サイズが変わったら再計算
			// mql.addEventListener(
			// 	'change',
			// 	checkWindow
			// );

		}
	);

	/**
	 * ============================================
	 * Resize
	 * ============================================
	 */
	// ウィンドウサイズ変更時の処理
	const onResize = () => {

		const width =
			container.clientWidth;

		const height =
			container.clientHeight;

		if (!width || !height) {
			return;
		}

		// カメラの比率を更新
		camera.aspect =
			width / height;

		camera.updateProjectionMatrix();

		// 描画サイズを更新
		renderer.setSize(
			width,
			height
		);
	};

	/**
	 * ============================================
	 * Animation
	 * ============================================
	 */

	// 自動回転用
	let autoRotationY = 0;

	const animate = () => {

		// 毎フレーム繰り返す
		requestAnimationFrame(animate);

		// 自動回転
		autoRotationY += 0.0006;

		const t = performance.now() * 0.0006;

		// 基本の上下運動
		const tilt =
			Math.sin(t * 1.0) * 0.14 - 0.03 +
			Math.sin(t * 0.28) * 0.05 +
			Math.sin(t * 0.09) * 0.02;

		// マウス追従がオンの場合
		if (enableMouseFollow) {
			currentMouseX += (targetMouseX - currentMouseX) * 0.05;
			currentMouseY += (targetMouseY - currentMouseY) * 0.05;

			rotateGroup.rotation.y =
				autoRotationY + currentMouseX * 0.3;

			// rotateGroup.rotation.x =
			// 	-0.2 + currentMouseY * 0.15;
			rotateGroup.rotation.x =
				-0.2 +
				currentMouseY * 0.15 +
				tilt;

		} else {

			// 自動回転のみ
			rotateGroup.rotation.y = autoRotationY;
			// rotateGroup.rotation.x = -0.2;
			rotateGroup.rotation.x = -0.2 + tilt;

		}

		// 現在の状態を画面へ描画
		renderer.render(
			scene,
			camera
		);

	};

	// アニメーション開始
	animate();

}

// ===============================================================
// 使用例
// ===============================================================
// ①別ページで使う場合
//
// import logoRotateAnimation from '../animation/logoRotateAnimation';
// logoRotateAnimation('serviceLogo');
//
// ②SVGだけ変更したい場合
//
// logoRotateAnimation(
// 'serviceLogo',
// 	'/images/service-logo.svg'
// );
